import { useMemo } from "react";
import type { ReactElement } from "react";
import { Tree } from "primereact/tree";
import type { TreeSelectionKeys } from "./datasetTreeNodes";
import { buildDatasetTreeNodes } from "./datasetTreeNodes";

/**
 * @property selectionKeys - Current checkbox state from PrimeReact Tree, or null if nothing selected
 * @property onChange - Called with updated selection keys when the user checks/unchecks any node
 * @property showEntityPlusDatasets - Whether to include Entity Plus-gated datasets (PoA, Officers, Entity Plus Details)
 * @property className - Optional CSS class override (defaults to "export-dataset-checklist")
 */
export interface DatasetChecklistProps {
  selectionKeys: TreeSelectionKeys | null;
  onChange: (selectionKeys: TreeSelectionKeys) => void;
  showEntityPlusDatasets: boolean;
  className?: string;
}

/**
 * Renders a PrimeReact Tree with tri-state checkboxes for selecting which
 * datasets to include in the entity export. Wraps {@link buildDatasetTreeNodes}
 * and memoizes the tree structure based on the Entity Plus flag.
 */
export function DatasetChecklist({
  selectionKeys,
  onChange,
  showEntityPlusDatasets,
  className,
}: DatasetChecklistProps): ReactElement {
  const nodes = useMemo(
    () => buildDatasetTreeNodes(showEntityPlusDatasets),
    [showEntityPlusDatasets],
  );

  return (
    <div className={className ?? "export-dataset-checklist"}>
      <Tree
        value={nodes}
        selectionMode="checkbox"
        selectionKeys={selectionKeys}
        onSelectionChange={(e) => onChange(e.value as TreeSelectionKeys)}
        className="export-dataset-tree"
      />
    </div>
  );
}

export default DatasetChecklist;
